/**
 * Dialog component for composing and sending admin notifications to users or groups.
 *
 * @module components/admin/Notification
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
    makeStyles,
} from '@material-ui/core/styles';
import {
    Grid,
    InputLabel,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextareaAutosize,
    Card,
    Typography,
    CardContent,
} from '@material-ui/core';
import moment from 'moment';
import TagInput from '../settings/TagInput';
import NotificationStyle from './Notification';
import {
    fetchUserList,
    fetchGroupList,
} from '../../redux/sims/actions';
import {fetchNotification, reset} from '../../redux/admin/action';
import {resetNotifications} from '../../redux/authentication/action';
import {SaveNotification} from '../../services/result.service';

/**
 * Component responsible for managing and displaying notifications.
 * This includes functionalities for sending a notification,
 * switching between group and individual notifications,
 * infinite scrolling for notification lists, and handling the user interface.
 *
 * @param {Object} props - Properties passed to the component.
 * @param {boolean} props.notification - Determines whether the notification dialog is open or closed.
 * @param {Function} props.notificationClose - Callback function to close the notification dialog.
 * @param {Function} props.fetchGroupList - Fetches the list of available groups.
 * @param {Function} props.fetchUserList - Fetches the list of available users.
 * @param {Array} props.groups - Array of available groups.
 * @param {Array} props.users - Array of available users.
 * @param {Function} props.fetchNotification - Fetches the notification list from the server.
 * @param {Array} props.notificationList - Array containing fetched notifications.
 * @param {number} props.total - Total number of notifications.
 * @param {Function} props.reset - Resets relevant data upon notification submission.
 * @param {Function} props.resetNotifications - Resets the list of existing notifications.
 *
 * @returns {JSX.Element} Returns a React functional component for notifications.
 */
const Notification = ({
    notification,
    notificationClose,
    fetchGroupList,
    fetchUserList,
    groups,
    users,
    fetchNotification,
    notificationList,
    total,
    resetNotifications,
}) => {
    const useStyles = makeStyles((theme) => NotificationStyle(theme));
    const [isGroup, setIsGroup] = useState(false);
    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(0);
    const [messages, setMessages] = useState([]);
    const [state, setState] = useState({
        notification: '',
        groups: [],
        receipts: [],
    });

    useEffect(() => {
        if (notificationList && notificationList.length) {
            setMessages((prev) => [...prev, ...notificationList]);
        }
    }, [notificationList]);

    useEffect(() => {
        if (notification) {
            fetchUserList();
        }
    }, [notification, fetchUserList]);

    useEffect(() => {
        setMessages([]);
        fetchNotification(pageNumber, true);
    }, [fetchNotification, pageNumber]);

    /**
     * Toggles the `isGroup` state and triggers the corresponding fetch function.
     *
     * The function checks the current value of `isGroup`. If `isGroup` is `false`,
     * it updates the state to `true` and invokes `fetchGroupList` to retrieve the list of groups.
     * If `isGroup` is `true`, it updates the state to `false` and invokes `fetchUserList`
     * to retrieve the list of users.
     */
    const groupChange = () => {
        if (isGroup === false) {
            setIsGroup(true);
            fetchGroupList();
        } else {
            setIsGroup(false);
            fetchUserList();
        }
    };

    /**
     * Updates the state based on whether the current selection is a group or not.
     * For group selections, updates the `groups` property with the given value and resets `receipts` to an empty array.
     * For non-group selections, updates the `receipts` property with the given value and resets `groups` to an empty array.
     *
     * @function
     * @param {any} value - The new value to be assigned to either `groups` or `receipts` depending on the `isGroup` flag.
     */
    const handleGroupChange = (value) => {
        if (isGroup) {
            setState({...state, groups: value, receipts: []});
        } else {
            setState({...state, groups: [], receipts: value});
        }
    };

    /**
     * Handles changes to form input fields by updating the component's state.
     *
     * @function handleChange
     * @param {Object} evt - The event object triggered by the input field change.
     * @param {Object} evt.target - The target element of the event.
     * @param {string} evt.target.name - The name attribute of the target element.
     * @param {string} evt.target.value - The value of the target element.
     * @description This function captures the 'name' and 'value' properties of the
     * event's target element and updates the corresponding state dynamically.
     */
    const handleChange = (evt) => {
        const {value} = evt.target;
        setState({...state, [evt.target.name]: value});
    };

    /**
     * Asynchronously handles the process of adding a notification.
     *
     * This function performs the following operations:
     * 1. Checks if a notification exists in the current state and if there are associated groups or receipts.
     * 2. Resets messages and sets the page number to zero.
     * 3. Saves the notification using the provided `SaveNotification` function.
     * 4. Resets the notifications within the current notification list by mapping their IDs.
     * 5. Fetches notifications for the first page.
     * 6. Updates the state by clearing the notification, receipts, and groups.
     * 7. Sets the group status to false.
     * 8. Closes the notification interface using the `notificationClose` function.
     *
     * This function relies on external functions like `SaveNotification`, `resetNotifications`, `fetchNotification`,
     * and `notificationClose` as well as the local state and predefined state management functions.
     *
     * @async
     * @returns {Promise<void>} Resolves when the notification handling process is completed.
     */
    const addNotification = async () => {
        if (state.notification && (state.groups.length || state.receipts.length)) {
            setMessages([]);
            setPageNumber(0);
            const pageNum = 0;
            await SaveNotification(state);
            //await reset();
            await resetNotifications(notificationList.map(notif => notif._id));
            await fetchNotification(pageNum, true);
            setState({
                ...state,
                notification: '',
                receipts: [],
                groups: [],
            });
            setIsGroup(false);
            notificationClose();
        }
    };

    /**
     * Updates the current page number by incrementing it by one.
     * Typically used for handling pagination changes.
     *
     * @function
     * @name handlePageChange
     * @returns {void} Does not return a value.
     */
    const handlePageChange = () => {
        setPageNumber((prevPageNumber) => prevPageNumber + 1);
    };

    return (
        <div>
            <Dialog
                open={notification}
                onClose={notificationClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="xs"
            >
                <DialogTitle id="alert-dialog-title">Add New Notification</DialogTitle>
                <DialogContent>
                    <Grid container className={classes.container}>
                        <Grid item xs={12} lg={12}>
                            <InputLabel className={classes.label}>Enter Notification Message</InputLabel>
                            <TextareaAutosize
                                minRows={5}
                                data-enable-grammarly="false"
                                name="notification"
                                onChange={handleChange}
                                className={!state.notification ? classes.error : classes.textArea}
                            />
                        </Grid>
                        {
                            isGroup ? (
                                <Grid item xs={12} lg={12}>
                                    <InputLabel className={classes.label}>
                                        3. Select Groups
                                        {' '}
                                        <span onClick={groupChange} className={classes.link}>
                                            (Or assign to a member)
                                        </span>
                                    </InputLabel>
                                </Grid>
                            ) : (
                                <Grid item xs={12} lg={12}>
                                    <InputLabel className={classes.label}>
                                        3. Select Members
                                        {' '}
                                        <span onClick={groupChange} className={classes.link}>
                                            (Or assign to a group)
                                        </span>
                                    </InputLabel>
                                </Grid>
                            )
                        }
                        {
                            isGroup
                            && (
                                <Grid
                                    item
                                    xs={12}
                                    lg={12}
                                    className={!state.groups.length ? classes.error : ''}
                                    style={{padding: 0}}
                                >
                                    <TagInput
                                        selectGroups={groups}
                                        name="groups"
                                        handleGroupChange={handleGroupChange}
                                        width="380px"
                                    />
                                </Grid>
                            )
                        }
                        {
                            !isGroup
                            && (
                                <Grid
                                    item
                                    xs={12}
                                    lg={12}
                                    className={!state.receipts.length ? classes.error : ''}
                                    style={{padding: 0}}
                                >
                                    <TagInput
                                        selectGroups={users}
                                        name="receipts"
                                        handleGroupChange={handleGroupChange}
                                        width="380px"
                                    />
                                </Grid>
                            )
                        }
                        <Grid item xs={12} lg={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                className={classes.notifyButton}
                                onClick={addNotification}
                            >
                                SEND NOTIFICATION
                            </Button>
                            <Button
                                variant="outlined"
                                className={classes.cancel}
                                disableElevation
                                color="primary"
                                onClick={notificationClose}
                            >
                                CANCEL
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
            <InfiniteScroll
                dataLength={messages.length}
                next={handlePageChange}
                hasMore={messages.length < total}
                loader={<p>Loading...</p>}
                style={{
                    height: 'none',
                    overflow: 'none',
                }}
            >
                <Grid container spacing={2} style={{marginTop: 12}}>
                    {
                        (messages.length > 0) && messages.map((notify) =>
                            <Grid item xs={12} key={`notification-list-${notify._id}`} id={`${notify._id}`}>
                                <Card className={classes.card}>
                                    <CardContent className={classes.content}>
                                        <Typography className={classes.message}>
                                            {notify.message}
                                        </Typography>
                                        <Grid container>
                                            <Grid item xs={2} className={classes.subDetails}>
                                                Date :
                                                {' '}
                                                {moment(notify.created_date).format('MM-DD-YYYY')}
                                            </Grid>
                                            <Grid item xs={2} className={classes.subDetails}>
                                                Sender :
                                                {' '}
                                                {notify.created_by}
                                            </Grid>
                                            <Grid item xs={4} className={classes.subDetails}>
                                                {notify.groups.length ? 'Receiver groups' : 'Recipient\'s Name'}
                                                {' '}
                                                :
                                                {' '}
                                                {notify.groups.length ? (notify.groups.toString()).replace(/,/g, ', ')
                                                    : (notify.recipients.toString()).replace(/,/g, ', ')}
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )
                    }
                </Grid>
            </InfiniteScroll>
            {
                messages.length === 0 && <h3>No Notification&apos;s added in your list yet!</h3>
            }
        </div>
    );
};
Notification.propTypes = {
    notification: PropTypes.bool,
    notificationClose: PropTypes.func,
    fetchGroupList: PropTypes.func,
    fetchUserList: PropTypes.func,
    groups: PropTypes.array,
    users: PropTypes.array,
    fetchNotification: PropTypes.func,
    notificationList: PropTypes.array,
    total: PropTypes.number,
    reset: PropTypes.func,
    resetNotifications: PropTypes.func,
};
const mapStateToProps = (state) => ({
    groups: state.sim.groups ? state.sim.groups : [],
    users: state.sim.users ? state.sim.users : [],
    notificationList: state.admin.notifications ?? state.admin.notifications.notifList ?? state.login.notifications ?? state.login.notifications.notifList ?? [],
    total: state.admin.notifications?.total ?? state.login.notifications?.total ?? 0,
});

const mapDispatchToProps = {
    fetchGroupList,
    fetchUserList,
    fetchNotification,
    reset,
    resetNotifications,
};

export default connect(mapStateToProps, mapDispatchToProps)(Notification);
